import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogActions, Box, Typography, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { toast } from 'react-hot-toast';
import { SimplyTestApi } from '../../api/simplytest';
import { TermsAcceptanceStatus } from '../../api/types';
import { selectConfig } from '../redux/slice';
import { selectIdToken, selectUserOrgId, selectUserEmail } from '../../auth/redux/slice';
import { IConfiguration } from '../types';
import { ROUTES } from '../constants';

// Aug 2026 - same masked-summary pattern StripeSetupForm already uses ("Visa ending in 4242" /
// "Chase ending in 6789") - deliberately limited to brand/bank + last 4, never the raw Stripe IDs.
const formatMaskedPaymentMethod = (status: TermsAcceptanceStatus): string => {
    if (status.paymentType === 'CC' && status.cardBrand && status.last4Digits)
        return `${status.cardBrand} ending in ${status.last4Digits}`;
    if (status.paymentType === 'ACH' && status.bankName && status.last4Digits)
        return `${status.bankName} ending in ${status.last4Digits}`;
    return 'On file - details limited';
};

// Aug 2026 - a hard-block, app-wide popup: an org that uses Stripe billing and has a saved payment
// method must have SOMEONE at that org accept billing terms before anyone at that org can use the
// app. Mirrors SystemBanner's self-fetching pattern (rendered once in AuthenticatedLayout, re-runs
// whenever idToken changes - which covers both a fresh login and an org switch, since both mint a
// new token). Checked per ORG (any acceptance row for the org at all), not per user - once one
// person accepts, nobody else at that org is ever prompted again. See
// TermsConditionsAcceptanceController.IsAcceptanceRequired server-side.
//
// Deliberately no close (X) button and no backdrop-dismiss - accepting is the only way past this
// for a flagged org. The one escape hatch is Sign Out, so a user who isn't the right person to
// accept billing terms isn't literally trapped in the tab - they just can't use the app until
// someone at their org (any user) accepts.
export const StripeTermsAcceptanceGate: React.FC<{}> = () => {
    const config: IConfiguration = useSelector(selectConfig)!;
    const idToken = useSelector(selectIdToken);
    const orgId = useSelector(selectUserOrgId);
    const email = useSelector(selectUserEmail);
    const client = useMemo(() => new SimplyTestApi(config.apiBaseUrl, idToken), [config.apiBaseUrl, idToken]);

    const [status, setStatus] = useState<TermsAcceptanceStatus | null>(null);
    const [isAccepting, setIsAccepting] = useState(false);
    const navigate = useNavigate();
    const required = status?.requiresAcceptance === true;

    useEffect(() => {
        if (!idToken) {
            setStatus(null);
            return;
        }
        let cancelled = false;
        client.getTermsAcceptanceRequired()
            .then(response => {
                if (!cancelled) setStatus(response.status === 200 ? response.data : null);
            })
            .catch(() => {
                // Fail closed on the UI (don't block the app over a network hiccup) - worst case
                // the popup shows on a later page load/refresh once the check succeeds.
                if (!cancelled) setStatus(null);
            });
        return () => { cancelled = true; };
    }, [client, idToken]);

    const handleAccept = async () => {
        if (!orgId) return;
        setIsAccepting(true);
        try {
            const response = await client.sendTermsConditionsAcceptance({ orgId: Number(orgId), userName: email ?? '', dateAccepted: null });
            if (response.status < 200 || response.status >= 300) {
                toast.error('Unable to record your acceptance - please try again.');
                return;
            }
            setStatus(prev => (prev ? { ...prev, requiresAcceptance: false } : prev));
        } catch {
            toast.error('Unable to record your acceptance - please try again.');
        } finally {
            setIsAccepting(false);
        }
    };

    const handleSignOut = () => navigate(ROUTES.SignOut);

    return (
        <Dialog open={required} disableEscapeKeyDown maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 24px 0',
            }}>
                <Box sx={{
                    width: '38px', height: '38px', borderRadius: '10px', backgroundColor: '#EEF2FF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    '& svg': { fontSize: '20px', color: '#4F46E5' },
                }}>
                    <GavelOutlinedIcon />
                </Box>
                <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#0D1B2A' }}>
                    Billing Terms &amp; Conditions
                </Typography>
            </Box>
            <DialogContent sx={{ padding: '16px 24px 8px' }}>
                <Typography sx={{ fontSize: '13.5px', color: '#334155', lineHeight: 1.6, mb: '12px' }}>
                    Your organization bills through a saved Stripe payment method. Before anyone at your
                    organization can continue using the portal, someone needs to review and accept the
                    billing terms below.
                </Typography>

                {status && (
                    <Box sx={{
                        backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px',
                        padding: '12px 14px', mb: '14px', display: 'flex', flexDirection: 'column', gap: '6px',
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                            <Typography sx={{ fontSize: '11.5px', color: '#64748B' }}>Organization</Typography>
                            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#0D1B2A' }}>{status.organizationName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                            <Typography sx={{ fontSize: '11.5px', color: '#64748B' }}>Payment method</Typography>
                            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#0D1B2A' }}>{formatMaskedPaymentMethod(status)}</Typography>
                        </Box>
                    </Box>
                )}

                <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Typography component="li" sx={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        You authorize Simply Test to automatically charge the payment method on file for
                        orders and fees placed under your organization's account.
                    </Typography>
                    <Typography component="li" sx={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        Charges are billed as orders are placed or as otherwise described at checkout - no
                        separate invoice or approval step follows.
                    </Typography>
                    <Typography component="li" sx={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        You can update or remove the saved payment method at any time from your organization's
                        billing settings.
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '11.5px', color: '#94A3B8', mt: '14px' }}>
                    This only needs to happen once per organization - once accepted, nobody else here will
                    see this again.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ padding: '12px 24px 22px', justifyContent: 'space-between' }}>
                <Button onClick={handleSignOut} sx={{ color: '#8B99A6', fontSize: '12.5px', fontWeight: 600, textTransform: 'none' }}>
                    Sign Out
                </Button>
                <LoadingButton
                    loading={isAccepting} onClick={handleAccept} variant="contained"
                    sx={{
                        height: '38px', borderRadius: '8px', backgroundColor: '#0891B2', fontSize: '13px',
                        fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
                        '&:hover': { backgroundColor: '#0E7490', boxShadow: 'none' },
                    }}
                >
                    I Accept
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
